import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { trpc } from '~/api/trpc';

type ConfigFormData = {
    pageTitleDe: string;
    pageTitleEn: string;
    progressDisplayDe: string;
    progressDisplayEn: string;
    noTimeslotsMessageDe: string;
    noTimeslotsMessageEn: string;
    selectTimeButtonTextDe: string;
    selectTimeButtonTextEn: string;
    waitlistTitleDe: string;
    waitlistTitleEn: string;
    waitlistDescriptionDe: string;
    waitlistDescriptionEn: string;
    waitlistButtonTextDe: string;
    waitlistButtonTextEn: string;
    morningLabelDe: string;
    morningLabelEn: string;
    afternoonLabelDe: string;
    afternoonLabelEn: string;
    eveningLabelDe: string;
    eveningLabelEn: string;
    continueButtonTextDe: string;
    continueButtonTextEn: string;
    // New fields
    waitlistLabelDe: string;
    waitlistLabelEn: string;
    waitlistEmailPlaceholderDe: string;
    waitlistEmailPlaceholderEn: string;
    addAnotherTreatmentTextDe: string;
    addAnotherTreatmentTextEn: string;
    waitlistFormTitleDe: string;
    waitlistFormTitleEn: string;
    waitlistFormSubtitleDe: string;
    waitlistFormSubtitleEn: string;
    availableAppointmentsLabelDe: string;
    availableAppointmentsLabelEn: string;
    showMoreTextDe: string;
    showMoreTextEn: string;
    showLessTextDe: string;
    showLessTextEn: string;
};

const CalendarSelectionCMS = () => {
    const [formData, setFormData] = useState<ConfigFormData>({
        pageTitleDe: '',
        pageTitleEn: '',
        progressDisplayDe: '',
        progressDisplayEn: '',
        noTimeslotsMessageDe: '',
        noTimeslotsMessageEn: '',
        selectTimeButtonTextDe: '',
        selectTimeButtonTextEn: '',
        waitlistTitleDe: '',
        waitlistTitleEn: '',
        waitlistDescriptionDe: '',
        waitlistDescriptionEn: '',
        waitlistButtonTextDe: '',
        waitlistButtonTextEn: '',
        morningLabelDe: '',
        morningLabelEn: '',
        afternoonLabelDe: '',
        afternoonLabelEn: '',
        eveningLabelDe: '',
        eveningLabelEn: '',
        continueButtonTextDe: '',
        continueButtonTextEn: '',
        // New fields
        waitlistLabelDe: '',
        waitlistLabelEn: '',
        waitlistEmailPlaceholderDe: '',
        waitlistEmailPlaceholderEn: '',
        addAnotherTreatmentTextDe: '',
        addAnotherTreatmentTextEn: '',
        waitlistFormTitleDe: '',
        waitlistFormTitleEn: '',
        waitlistFormSubtitleDe: '',
        waitlistFormSubtitleEn: '',
        availableAppointmentsLabelDe: '',
        availableAppointmentsLabelEn: '',
        showMoreTextDe: '',
        showMoreTextEn: '',
        showLessTextDe: '',
        showLessTextEn: '',
    });
    const [isDirty, setIsDirty] = useState(false);

    const utils = trpc.useUtils();
    const { data: configData, isLoading } = trpc.bookingFunnelCMS.getCalendarSelectionConfig.useQuery();

    const updateMutation = trpc.bookingFunnelCMS.updateCalendarSelectionConfig.useMutation({
        onSuccess: () => {
            toast.success('Konfiguration gespeichert');
            setIsDirty(false);
            utils.bookingFunnelCMS.getCalendarSelectionConfig.invalidate();
        },
        onError: (error) => {
            toast.error('Fehler beim Speichern', { description: error.message });
        },
    });

    useEffect(() => {
        if (configData?.data) {
            const config = configData.data;
            setFormData({
                pageTitleDe: config.pageTitleDe,
                pageTitleEn: config.pageTitleEn,
                progressDisplayDe: config.progressDisplayDe,
                progressDisplayEn: config.progressDisplayEn,
                noTimeslotsMessageDe: config.noTimeslotsMessageDe,
                noTimeslotsMessageEn: config.noTimeslotsMessageEn,
                selectTimeButtonTextDe: config.selectTimeButtonTextDe,
                selectTimeButtonTextEn: config.selectTimeButtonTextEn,
                waitlistTitleDe: config.waitlistTitleDe,
                waitlistTitleEn: config.waitlistTitleEn,
                waitlistDescriptionDe: config.waitlistDescriptionDe,
                waitlistDescriptionEn: config.waitlistDescriptionEn,
                waitlistButtonTextDe: config.waitlistButtonTextDe,
                waitlistButtonTextEn: config.waitlistButtonTextEn,
                morningLabelDe: config.morningLabelDe,
                morningLabelEn: config.morningLabelEn,
                afternoonLabelDe: config.afternoonLabelDe,
                afternoonLabelEn: config.afternoonLabelEn,
                eveningLabelDe: config.eveningLabelDe,
                eveningLabelEn: config.eveningLabelEn,
                continueButtonTextDe: config.continueButtonTextDe,
                continueButtonTextEn: config.continueButtonTextEn,
                // New fields
                waitlistLabelDe: config.waitlistLabelDe ?? '',
                waitlistLabelEn: config.waitlistLabelEn ?? '',
                waitlistEmailPlaceholderDe: config.waitlistEmailPlaceholderDe ?? '',
                waitlistEmailPlaceholderEn: config.waitlistEmailPlaceholderEn ?? '',
                addAnotherTreatmentTextDe: config.addAnotherTreatmentTextDe ?? '',
                addAnotherTreatmentTextEn: config.addAnotherTreatmentTextEn ?? '',
                waitlistFormTitleDe: config.waitlistFormTitleDe ?? '',
                waitlistFormTitleEn: config.waitlistFormTitleEn ?? '',
                waitlistFormSubtitleDe: config.waitlistFormSubtitleDe ?? '',
                waitlistFormSubtitleEn: config.waitlistFormSubtitleEn ?? '',
                availableAppointmentsLabelDe: config.availableAppointmentsLabelDe ?? '',
                availableAppointmentsLabelEn: config.availableAppointmentsLabelEn ?? '',
                showMoreTextDe: config.showMoreTextDe ?? '',
                showMoreTextEn: config.showMoreTextEn ?? '',
                showLessTextDe: config.showLessTextDe ?? '',
                showLessTextEn: config.showLessTextEn ?? '',
            });
        }
    }, [configData]);

    const handleChange = (field: keyof ConfigFormData, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setIsDirty(true);
    };

    const handleSave = () => {
        updateMutation.mutate(formData);
    };

    if (isLoading) {
        return <Skeleton className='h-96 w-full' />;
    }

    const renderFieldPair = (labelDe: string, labelEn: string, fieldDe: keyof ConfigFormData, fieldEn: keyof ConfigFormData, multiline = false) => (
        <div className='grid grid-cols-2 gap-4'>
            <div className='space-y-2'>
                <Label>{labelDe}</Label>
                {multiline ? (
                    <Textarea
                        value={formData[fieldDe]}
                        onChange={(e) => handleChange(fieldDe, e.target.value)}
                        rows={3}
                    />
                ) : (
                    <Input
                        value={formData[fieldDe]}
                        onChange={(e) => handleChange(fieldDe, e.target.value)}
                    />
                )}
            </div>
            <div className='space-y-2'>
                <Label>{labelEn}</Label>
                {multiline ? (
                    <Textarea
                        value={formData[fieldEn]}
                        onChange={(e) => handleChange(fieldEn, e.target.value)}
                        rows={3}
                    />
                ) : (
                    <Input
                        value={formData[fieldEn]}
                        onChange={(e) => handleChange(fieldEn, e.target.value)}
                    />
                )}
            </div>
        </div>
    );

    return (
        <Card>
            <CardHeader>
                <CardTitle>Kalender-Auswahl CMS</CardTitle>
                <CardDescription>
                    Verwalten Sie die Texte und Beschriftungen für die Terminauswahl-Seite.
                </CardDescription>
            </CardHeader>
            <CardContent className='space-y-6'>
                <div className='flex justify-end'>
                    <Button onClick={handleSave} disabled={!isDirty || updateMutation.isPending}>
                        {updateMutation.isPending ? (
                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                        ) : (
                            <Save className='mr-2 h-4 w-4' />
                        )}
                        Speichern
                    </Button>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle className='text-lg'>Seiten-Header</CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-4'>
                        {renderFieldPair('Seitentitel (DE)', 'Page Title (EN)', 'pageTitleDe', 'pageTitleEn')}
                        {renderFieldPair('Fortschrittsanzeige (DE)', 'Progress Display (EN)', 'progressDisplayDe', 'progressDisplayEn')}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className='text-lg'>Terminauswahl</CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-4'>
                        {renderFieldPair('Keine Termine Nachricht (DE)', 'No Timeslots Message (EN)', 'noTimeslotsMessageDe', 'noTimeslotsMessageEn')}
                        {renderFieldPair('Termin-Auswahl Button (DE)', 'Select Time Button (EN)', 'selectTimeButtonTextDe', 'selectTimeButtonTextEn')}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className='text-lg'>Tageszeit-Labels</CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-4'>
                        {renderFieldPair('Vormittag (DE)', 'Morning (EN)', 'morningLabelDe', 'morningLabelEn')}
                        {renderFieldPair('Nachmittag (DE)', 'Afternoon (EN)', 'afternoonLabelDe', 'afternoonLabelEn')}
                        {renderFieldPair('Abend (DE)', 'Evening (EN)', 'eveningLabelDe', 'eveningLabelEn')}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className='text-lg'>Warteliste</CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-4'>
                        {renderFieldPair('Warteliste Titel (DE)', 'Waitlist Title (EN)', 'waitlistTitleDe', 'waitlistTitleEn')}
                        {renderFieldPair('Warteliste Beschreibung (DE)', 'Waitlist Description (EN)', 'waitlistDescriptionDe', 'waitlistDescriptionEn', true)}
                        {renderFieldPair('Warteliste Button (DE)', 'Waitlist Button (EN)', 'waitlistButtonTextDe', 'waitlistButtonTextEn')}
                        {renderFieldPair('Warteliste Label (DE)', 'Waitlist Label (EN)', 'waitlistLabelDe', 'waitlistLabelEn')}
                        {renderFieldPair('Warteliste E-Mail Placeholder (DE)', 'Waitlist Email Placeholder (EN)', 'waitlistEmailPlaceholderDe', 'waitlistEmailPlaceholderEn')}
                        {renderFieldPair('Warteliste Formular Titel (DE)', 'Waitlist Form Title (EN)', 'waitlistFormTitleDe', 'waitlistFormTitleEn')}
                        {renderFieldPair('Warteliste Formular Untertitel (DE)', 'Waitlist Form Subtitle (EN)', 'waitlistFormSubtitleDe', 'waitlistFormSubtitleEn', true)}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className='text-lg'>Termine & Navigation</CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-4'>
                        {renderFieldPair('Verfügbare Termine Label (DE)', 'Available Appointments Label (EN)', 'availableAppointmentsLabelDe', 'availableAppointmentsLabelEn')}
                        {renderFieldPair('Mehr anzeigen (DE)', 'Show More (EN)', 'showMoreTextDe', 'showMoreTextEn')}
                        {renderFieldPair('Weniger anzeigen (DE)', 'Show Less (EN)', 'showLessTextDe', 'showLessTextEn')}
                        {renderFieldPair('Weitere Behandlung hinzufügen (DE)', 'Add Another Treatment (EN)', 'addAnotherTreatmentTextDe', 'addAnotherTreatmentTextEn')}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className='text-lg'>Weiter-Button</CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-4'>
                        {renderFieldPair('Weiter-Button (DE)', 'Continue Button (EN)', 'continueButtonTextDe', 'continueButtonTextEn')}
                    </CardContent>
                </Card>
            </CardContent>
        </Card>
    );
};

export default CalendarSelectionCMS;
